import { useEffect, useState } from "react";
import { Plus, Pencil, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", code: "", description: "" };

export default function KategoriKeahlian() {
  const { user } = useAuth();
  const canManage = ["super_admin", "secretariat", "president"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/categories", { params: { include_inactive: true } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (c) => { setEditingId(c.id); setForm({ name: c.name, code: c.code, description: c.description || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/categories/${editingId}`, form);
        toast.success("Kategori dikemas kini");
      } else {
        await api.post("/categories", form);
        toast.success("Kategori ditambah");
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Kategori dinyahaktifkan");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reactivate = async (c) => {
    try {
      await api.put(`/categories/${c.id}/activate`);
      toast.success("Kategori diaktifkan semula");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-kategori-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-piks-black">Kategori Keahlian</h1>
          <p className="text-sm text-piks-muted">Urus kategori keahlian PPPS.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
          <DialogTrigger asChild>
            {canManage && <Button data-testid="add-category-btn" onClick={openCreate} className="bg-piks-black hover:bg-piks-black/90"><Plus className="w-4 h-4 mr-1.5" /> Tambah Kategori</Button>}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editingId ? "Edit Kategori" : "Kategori Baharu"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Nama</Label><Input data-testid="category-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Kod</Label><Input data-testid="category-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Keterangan</Label><Input data-testid="category-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
              <Button type="submit" disabled={saving} data-testid="category-submit-btn" className="w-full bg-piks-black hover:bg-piks-black/90">{saving ? "Menyimpan..." : "Simpan"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className="p-5 rounded-xl bg-white border border-piks-border" data-testid={`category-item-${c.id}`}>
            <div className="flex justify-between items-start mb-2 gap-2">
              <p className="font-semibold text-piks-black">{c.name}</p>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${c.is_active ? "bg-emerald-50 text-emerald-700" : "bg-piks-border/40 text-piks-muted"}`}>{c.is_active ? "AKTIF" : "TIDAK AKTIF"}</span>
            </div>
            <p className="text-xs font-mono text-piks-muted mb-2">{c.code}</p>
            <p className="text-sm text-piks-muted mb-4">{c.description}</p>
            {canManage && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              {c.is_active ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="border-piks-red text-piks-red hover:bg-piks-red hover:text-white" data-testid={`deactivate-category-${c.id}`}>
                      <Ban className="w-3.5 h-3.5 mr-1.5" /> Nyahaktifkan
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`deactivate-category-dialog-${c.id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Nyahaktifkan "{c.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>Kategori ini tidak akan tersedia untuk ahli baharu. Ahli sedia ada dalam kategori ini kekal tidak terjejas.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Batal</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deactivate(c.id)} data-testid={`confirm-deactivate-category-${c.id}`} className="bg-piks-red hover:bg-piks-red/90">Nyahaktifkan</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
                <Button size="sm" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white" onClick={() => reactivate(c)} data-testid={`reactivate-category-${c.id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Aktifkan Semula
                </Button>
              )}
            </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

